/**
 * mapCapture — captures the current OL map + visible layers as a data URL image.
 *
 * OL10 approach:
 *  1. Find all <canvas> elements inside the map target
 *  2. Call map.renderSync() to force a full render
 *  3. Composite all canvases onto a single output canvas
 *  4. Return as data URL (JPEG)
 */

import type Map from 'ol/Map';

/**
 * Capture the current OL map view as a JPEG data URL.
 * Includes all raster and vector layers at the current zoom/extent.
 */
export async function captureMap(map: Map): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      map.renderSync();

      // Small timeout to let tiles finish loading
      setTimeout(() => {
        try {
          const target = map.getTargetElement() as HTMLElement;
          if (!target) { reject(new Error('No map target')); return; }

          const mapCanvases = target.querySelectorAll('canvas');
          if (mapCanvases.length === 0) { reject(new Error('No canvas found')); return; }

          // Create composite canvas at the map's pixel size
          const size = map.getSize();
          if (!size) { reject(new Error('No map size')); return; }

          const compositeCanvas = document.createElement('canvas');
          const pixelRatio = window.devicePixelRatio || 1;
          compositeCanvas.width = size[0] * pixelRatio;
          compositeCanvas.height = size[1] * pixelRatio;
          const ctx = compositeCanvas.getContext('2d')!;

          // Draw each canvas layer (base tiles, vector layers, etc.)
          mapCanvases.forEach((c) => {
            if (c.width > 0 && c.height > 0) {
              try {
                ctx.drawImage(c, 0, 0, compositeCanvas.width, compositeCanvas.height);
              } catch {
                // CORS-tainted canvas — skip
              }
            }
          });

          resolve(compositeCanvas.toDataURL('image/jpeg', 0.92));
        } catch (err) {
          reject(err);
        }
      }, 500);
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Capture a DOM element as a JPEG data URL using a canvas.
 * Works for simple elements; for complex HTML we render to an offscreen canvas.
 */
export async function captureElement(element: HTMLElement): Promise<string> {
  // Use a simple approach: render via foreignObject in SVG → canvas
  const rect = element.getBoundingClientRect();
  const w = rect.width;
  const h = rect.height;

  // Clone the element to avoid modifying the DOM
  const clone = element.cloneNode(true) as HTMLElement;
  clone.style.position = 'absolute';
  clone.style.left = '-9999px';
  document.body.appendChild(clone);

  // Get computed styles as inline
  const xml = new XMLSerializer().serializeToString(clone);
  document.body.removeChild(clone);

  const svgStr = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
      <foreignObject width="100%" height="100%">
        <div xmlns="http://www.w3.org/1999/xhtml">${xml}</div>
      </foreignObject>
    </svg>`;

  const svgBlob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = w * 2;
      canvas.height = h * 2;
      const ctx = canvas.getContext('2d')!;
      ctx.scale(2, 2);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.9));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('SVG to canvas failed'));
    };
    img.src = url;
  });
}

/**
 * Capture the BI dashboard container as a data URL.
 * Uses the same foreignObject approach but at higher quality.
 */
export async function captureDashboard(container: HTMLElement): Promise<string> {
  const w = container.scrollWidth;
  const h = container.scrollHeight;

  const canvas = document.createElement('canvas');
  const scale = 2;
  canvas.width = w * scale;
  canvas.height = h * scale;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(scale, scale);

  // White background
  ctx.fillStyle = '#0f1117';
  ctx.fillRect(0, 0, w, h);

  // For each DraggableCard, find it and draw its rendered content
  const cards = container.querySelectorAll<HTMLElement>('[class*="absolute"]');
  for (const card of cards) {
    const rect = card.getBoundingClientRect();
    const contRect = container.getBoundingClientRect();
    const x = rect.left - contRect.left + container.scrollLeft;
    const y = rect.top - contRect.top + container.scrollTop;

    // Try to find canvas elements (recharts renders to SVG, not canvas)
    // For recharts SVGs, we need to serialize and draw them
    const svgs = card.querySelectorAll('svg');
    const cardCanvas = document.createElement('canvas');
    cardCanvas.width = rect.width * scale;
    cardCanvas.height = rect.height * scale;
    const cardCtx = cardCanvas.getContext('2d')!;
    cardCtx.scale(scale, scale);

    // Draw card background
    cardCtx.fillStyle = 'rgba(31, 41, 55, 0.95)';
    cardCtx.roundRect(0, 0, rect.width, rect.height, 8);
    cardCtx.fill();

    // Draw card header
    const header = card.querySelector('[class*="cursor-move"]') as HTMLElement | null;
    if (header) {
      cardCtx.fillStyle = 'rgba(55, 65, 81, 0.5)';
      cardCtx.fillRect(0, 0, rect.width, 28);
      cardCtx.fillStyle = '#d1d5db';
      cardCtx.font = 'bold 10px Helvetica, Arial, sans-serif';
      cardCtx.textAlign = 'left';
      const titleEl = header.querySelector('span');
      if (titleEl) {
        cardCtx.fillText(titleEl.textContent || '', 24, 18);
      }
    }

    // Draw SVG charts
    for (const svg of svgs) {
      try {
        const svgRect = svg.getBoundingClientRect();
        const svgX = svgRect.left - rect.left;
        const svgY = svgRect.top - rect.top;

        const svgStr = new XMLSerializer().serializeToString(svg);
        const svgBlob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl = URL.createObjectURL(svgBlob);
        const img = await loadImageAsync(svgUrl);
        cardCtx.drawImage(img, svgX, svgY, svgRect.width, svgRect.height);
        URL.revokeObjectURL(svgUrl);
      } catch { /* skip */ }
    }

    // Draw tables
    const tables = card.querySelectorAll('table');
    if (tables.length > 0) {
      // Draw a simplified version of the table
      const table = tables[0];
      const rows = table.querySelectorAll('tr');
      let rowY = 32; // start below header
      cardCtx.font = '9px Helvetica, Arial, sans-serif';

      rows.forEach((row, ri) => {
        const cells = row.querySelectorAll('th, td');
        let cellX = 4;
        cells.forEach((cell) => {
          const text = (cell as HTMLElement).textContent || '';
          cardCtx.fillStyle = ri === 0 ? '#9ca3af' : '#d1d5db';
          if (ri === 0) cardCtx.font = 'bold 9px Helvetica, Arial, sans-serif';
          else cardCtx.font = '9px Helvetica, Arial, sans-serif';
          cardCtx.textAlign = 'left';
          cardCtx.fillText(text, cellX, rowY);
          cellX += Math.max(60, text.length * 6 + 8);
        });
        rowY += 14;
        if (rowY > rect.height - 10) return;
      });
    }

    // Draw KPI cards (text-heavy, no SVG)
    const kpiValue = card.querySelector('.text-3xl');
    if (kpiValue) {
      cardCtx.fillStyle = '#3b82f6';
      cardCtx.font = 'bold 28px Helvetica, Arial, sans-serif';
      cardCtx.textAlign = 'center';
      cardCtx.fillText(kpiValue.textContent || '', rect.width / 2, rect.height / 2);
    }

    ctx.drawImage(cardCanvas, x, y, rect.width, rect.height);
  }

  return canvas.toDataURL('image/jpeg', 0.92);
}

function loadImageAsync(src: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
}
